import { api } from '@/api/client'

/**
 * Trigger a save-to-disk download for a JWT-gated file URL.
 *
 * Why this exists: every protected endpoint sits behind the
 * Authorization: Bearer header, but a plain `window.location.href = url`
 * navigation drops headers (browsers can't attach Authorization to
 * top-level navigations). The server then 401s and the user sees
 * the raw "missing_token" envelope instead of a download.
 *
 * The fix is to fetch the bytes via the api() helper (which attaches
 * the bearer + transparently refreshes on 401), buffer them as a
 * Blob, and trigger the download via an in-memory object URL.
 *
 * `path` may include the /api/v1 prefix (the server emits absolute
 * paths in download_url fields) — it's stripped before dispatch
 * because buildUrl re-adds it.
 */
export async function downloadAuthed(path: string, filename: string): Promise<void> {
  const relative = path.replace(/^\/api\/v1/, '')
  const res = await api(relative)
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  // Prefer the server-side filename — the reports handler stamps a
  // descriptive name (e.g. "monthly-leadership-pack_2026-04-01_to_2026-04-30.html")
  // via Content-Disposition. The caller-supplied `filename` is the
  // fallback for endpoints that don't set the header.
  const finalName = filenameFromContentDisposition(res.headers.get('Content-Disposition')) ?? filename
  const a = document.createElement('a')
  a.href = url
  a.download = finalName
  document.body.appendChild(a)
  a.click()
  a.remove()
  // Safari needs a tick before it commits the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// filenameFromContentDisposition extracts the filename from a
// Content-Disposition header. Handles both the legacy `filename="x"`
// and the RFC 5987 `filename*=UTF-8''<percent-encoded>` form.
// Returns null when the header is absent or unparseable.
function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null
  // RFC 5987 first — wins when both forms are present.
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(header)
  if (star && star[1]) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''))
    } catch {
      // Falls through to the legacy form.
    }
  }
  const legacy = /filename\s*=\s*"?([^";]+)"?/i.exec(header)
  if (legacy && legacy[1]) {
    return legacy[1].trim()
  }
  return null
}
